import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Lightbulb } from "lucide-react";

interface ValuationMethodWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  currentMethod: string;
  newMethod: string;
}

export function ValuationMethodWarningDialog({
  open,
  onOpenChange,
  onConfirm,
  currentMethod,
  newMethod,
}: ValuationMethodWarningDialogProps) {
  const getMethodDisplayName = (method: string) => {
    switch (method) {
      case "average":
        return "Average Cost (Weighted Average)";
      case "fifo":
        return "FIFO (First In, First Out)";
      case "standard":
        return "Standard Cost";
      default:
        return method;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <AlertDialogTitle className="text-xl">
                Change Valuation Method?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base mt-1">
                This is a critical accounting setting change
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm">
                <p className="font-semibold text-yellow-900">
                  You are about to change the valuation method from:
                </p>
                <div className="space-y-1 ml-4">
                  <p className="text-yellow-800">
                    <span className="font-medium">Current:</span> {getMethodDisplayName(currentMethod)}
                  </p>
                  <p className="text-yellow-800">
                    <span className="font-medium">New:</span> {getMethodDisplayName(newMethod)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p className="font-semibold text-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Important Considerations:
            </p>
            <ul className="space-y-2 ml-6 list-disc">
              <li>
                <strong>Historical Data:</strong> Changing the valuation method will affect how future inventory costs are calculated. Historical transactions will remain unchanged.
              </li>
              <li>
                <strong>Financial Reporting:</strong> This change may impact your Cost of Goods Sold (COGS), inventory valuation on the balance sheet, and profit margins.
              </li>
              <li>
                <strong>Accounting Standards:</strong> Ensure this change complies with your accounting standards and has been approved by your financial controller or accountant.
              </li>
              <li>
                <strong>Tax Implications:</strong> Consult with your tax advisor as this change may have tax reporting implications.
              </li>
              <li>
                <strong>Audit Trail:</strong> This change will be logged for audit purposes.
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-blue-600 text-sm">
                <p className="font-semibold mb-2">Best Practice:</p>
                <p>
                  Valuation method changes should ideally be made at fiscal year-end or during system setup before recording transactions. Consider consulting with your accounting team before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-600"
          >
            I Understand, Proceed with Change
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
